import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../../components/Settings/SettingsPageContainer"
import {useSelector} from "react-redux"
import {useRPC} from "../../../../../hooks/rpc"
import {useLocalization} from "../../../../../hooks/localization"
import {ColumnModel, SettingsList} from "../../../../../components/Settings/SettingsList"
import {useSetSelector} from "../../../../../hooks/set-selector"
import {
    CONTROL_TYPE_ID_ALARM,
    CONTROL_TYPE_ID_BELL,
    CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON,
    CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN,
    CONTROL_TYPE_ID_SIREN,
    ControlModel,
    SOUND_LEVEL_TYPE_ID,
    SOUND_PLAYED_TYPE_ID
} from "../../../../../types/control"
import {UpDown} from "../components/UpDown"
import {useQuestion} from "../../../../../hooks/question"
import {changeBusyAction} from '../../../../../store/ui/actions';
import {useDispatch} from 'react-redux';

const keyExtractor = (item: ControlModel) => item.id

export const ControlSortingScreen = memo(function ControlSortingScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const question = useQuestion()
    const dispatch = useDispatch();
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList)
    const controlSet = useSetSelector(state => state.control.controlList)
    const controlList = useSelector(state => state.control.controlList)

    const sortedControlList = useMemo(
        () =>
            controlList
                .filter(
                    items =>
                        items?.parameters?.["order"] &&
                        items?.type_id !== CONTROL_TYPE_ID_ALARM &&
                        items?.type_id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON &&
                        items?.type_id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN &&
                        items?.type_id !== CONTROL_TYPE_ID_SIREN &&
                        items?.type_id !== CONTROL_TYPE_ID_BELL &&
                        items?.type_id !== SOUND_LEVEL_TYPE_ID &&
                        items?.type_id !== SOUND_PLAYED_TYPE_ID
                )
                .sort((a, b) => a?.parameters?.["order"] - b?.parameters?.["order"]),
        [controlList]
    )

    const [selectedItem, setSelectedItem] = useState<ControlModel>(null)

    const swapItemOrders = useCallback(
        (itemA: ControlModel, itemB: ControlModel) => {
            dispatch(changeBusyAction(true))
            Promise.all([
                rpc.updateChangeOrder({
                    relational_id: itemA.id,
                    is_auto_sorting: false,
                    type: "control",
                    order: itemB.parameters?.["order"]
                }),
                rpc.updateChangeOrder({
                    relational_id: itemB.id,
                    is_auto_sorting: false,
                    type: "control",
                    order: itemA.parameters?.["order"]
                })
            ]).then(() => {
              setSelectedItem(null)
              dispatch(changeBusyAction(false))
            })
        },
        [rpc]
    )

    const onUpClick = useCallback(
        (index: number) => {
            swapItemOrders(sortedControlList[index - 1], sortedControlList[index])
        },
        [sortedControlList, swapItemOrders]
    )

    const onDownClick = useCallback(
        (index: number) => {
            swapItemOrders(sortedControlList[index + 1], sortedControlList[index])
        },
        [sortedControlList, swapItemOrders]
    )

    const displayNameExtractor = useCallback(
        (item: ControlModel, column: ColumnModel, index?: number) => {
            if (column.key === "name") {
                return controlSet[item.id]?.name || "-"
            } else if (column.key === "control_type") {
                return controlTypeSet[item?.type_id]?.name || "-"
            } else if (column.key === "order") {
                return (
                    <UpDown
                        index={index}
                        onUpClick={index === 0 ? undefined : onUpClick}
                        onDownClick={index === sortedControlList.length - 1 ? undefined : onDownClick}
                    />
                )
            }
            return item[column.key] || "-"
        },
        [onUpClick, onDownClick, controlSet, controlTypeSet, sortedControlList]
    )

    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 3,
                name: t("name"),
                key: "name"
            },
            {
                length: 2,
                name: t("control_type"),
                key: "control_type"
            },
            {
                length: 2,
                name: t("order"),
                key: "order"
            }
        ],
        [t]
    )
    const onClickAutoSorting = useCallback(() => {
        return question(t("are_you_sure"), () => {
            return rpc.updateChangeOrder({
                is_auto_sorting: true,
                type: "control"
            })
        })
    }, [question, t, rpc])

    const buttons = useMemo(
        () => [
            {
              text: "A-Z (Auto)",
              className: "blue",
              onClick: onClickAutoSorting
            }
        ],
        [onClickAutoSorting]
    )

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings/custom-menu/"}>
                <SettingsList
                    sortDisabled={true}
                    columns={columns}
                    items={sortedControlList}
                    keyExtractor={keyExtractor}
                    displayNameExtractor={displayNameExtractor}
                    selectedItem={selectedItem}
                    onItemSelected={setSelectedItem}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
